import React, { useEffect, useState, useContext, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import moment from 'moment';
import * as api from '../../utils/api';
import analytics from '../../services/analytics';
import { RESET_COURSE, SET_CODEPROMO } from '../../actions/course';
import { buildDevisSelectionPayload } from '../../utils/devisSelectionPayload';
import { buildBillingAddressPayload } from '../../utils/billingAddressPayload';
import buildCourseDate, { toCourseDay } from '../../utils/courseDate';
import { resolveBilledParty } from '../../utils/billedParty';
import { selectCapabilities, selectQuestionsLight } from '../../reducers/diag';
import PartenaireContext from '../../contexts/PartenaireContext';
import useResaShop from '../../hooks/useResaShop';
import { FeaturesContext } from '../../contexts/FeaturesContext';
import useEstimatedDuration from '../../hooks/useEstimatedDuration';
import useOverlappingCourse from '../../hooks/useOverlappingCourse';
import { useCurrentZone } from '../../hooks/useZones';
import buildCourseCreationErrorModal from '../../utils/courseCreationError';
import CGV_URL from '../../utils/cgvUrl';
import {
  aggloSlots,
  diagToText,
  getEstimatedDuration,
  GTMAnalyticsSendDL,
  getLang,
  getVersion,
} from '../../utils/utils';
import {
  logPaymentStep,
  logPaymentError,
  setPaymentUserContext,
  createPaymentSessionId,
  getPaymentSessionId,
  clearPaymentSession,
  PAYMENT_STEPS,
} from '../../utils/paymentLogger';

// Components
import Box from '../../components/shared/atoms/Box';
import Row from '../../components/shared/atoms/Row';
import Heading from '../../components/shared/atoms/Heading';
import Text from '../../components/shared/atoms/Text';
import Button from '../../components/shared/atoms/Button';
import Link from '../../components/shared/atoms/Link';
import Input from '../../components/shared/atoms/Input';
import Container from '../../components/shared/atoms/Container';
import ProgressSideBar from '../../components/repair/ProgressSideBar';
import BillingAddressPicker from '../../components/repair/BillingAddressPicker';
import useModal from '../../hooks/useModal';
import SurprisedFaceColor56 from '../../components/icons/SurprisedFaceColor56';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
  margin-right: 8px;
`;

const FirstLetterUpperCase = styled.span`
  text-transform: capitalize;
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Modern card container with shadow
const MainCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  padding: 32px;
  max-width: 600px;
  margin: 0 auto;
  animation: ${slideIn} 0.4s ease-out;

  @media (max-width: ${(p) => p.theme.breakpoints.md}) {
    padding: 24px;
    border-radius: 12px;
  }
`;

// Summary item with icon
const SummaryItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const SummaryIcon = styled.div`
  font-size: 24px;
  flex-shrink: 0;
  margin-top: 2px;
`;

const SummaryContent = styled.div`
  flex: 1;
`;

const SummaryLabel = styled.div`
  font-family: 'LTSaeada', Arial, sans-serif;
  font-size: 0.85rem;
  color: #6b7280;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
`;

const SummaryValue = styled.div`
  font-family: 'LTSaeada', Arial, sans-serif;
  font-size: 1rem;
  color: #1f2937;
  font-weight: 600;
  line-height: 1.5;
`;

// Primary CTA button
const PrimaryCTA = styled(Button)`
  width: 100%;
  font-family: 'LTSaeada', Arial, sans-serif;
  font-weight: 600;
  font-size: 18px;
  padding: 18px 32px;
  background: #d5ff4f;
  color: #1f2937;
  border: none;
  border-radius: 12px;
  transition: all 0.2s ease;
  box-shadow: 0 4px 14px rgba(213, 255, 79, 0.4);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: #c5ef3f;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(213, 255, 79, 0.5);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background: #d1d5db;
    color: #6b7280;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

// Fixed footer for mobile only
const MobileFixedFooter = styled.div`
  display: none;

  @media (max-width: ${(p) => p.theme.breakpoints.md}) {
    display: block;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: white;
    padding: 16px 20px;
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
    z-index: 1000;
  }
`;

// FIX-895: the CGV sentence makes the fixed footer up to ~200px tall (320px
// wide, larger text), more than the 80px margin below reserves.
const MobileFooterSpacer = styled.div`
  display: none;

  @media (max-width: ${(p) => p.theme.breakpoints.md}) {
    display: block;
    height: 100px;
  }
`;

// Hide inline CTA on mobile (shown in fixed footer instead)
const InlineCTAWrapper = styled.div`
  @media (max-width: ${(p) => p.theme.breakpoints.md}) {
    display: none;
  }
`;

// FIX-895 — the CGV (art. 6.1.4, 6.7.3, 6.7.5) quote this sentence word for
// word: changing it means changing the CGV.

const CgvNotice = styled.p`
  margin: 12px 0 0;
  font-size: 0.8rem;
  line-height: 1.4;
  color: #6b7280;
  text-align: center;

  a {
    color: inherit;
    text-decoration: underline;
  }

  @media (max-width: ${(p) => p.theme.breakpoints.md}) {
    margin-top: 8px;
    font-size: 0.7rem;
    line-height: 1.3;
  }
`;

function CgvConsent() {
  return (
    <CgvNotice data-testid="cgv-consent">
      En confirmant, j'accepte les{' '}
      <a href={CGV_URL} target="_blank" rel="noopener noreferrer">
        conditions générales
      </a>{' '}
      et je demande que la réparation ait lieu avant la fin du délai de
      rétractation. Je reconnais perdre ce droit une fois la réparation
      entièrement effectuée.
    </CgvNotice>
  );
}

// Trust badges container
const TrustBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin-top: 24px;
`;

const TrustBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #f9fafb;
  border-radius: 8px;
  font-family: 'LTSaeada', Arial, sans-serif;
  font-size: 0.85rem;
  color: #4b5563;
  font-weight: 500;

  span {
    font-size: 18px;
  }
`;

// Promo code input group
const PromoCodeGroup = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #f0f0f0;
`;

const PromoLabel = styled.label`
  display: block;
  font-family: 'LTSaeada', Arial, sans-serif;
  font-size: 0.9rem;
  color: #4b5563;
  margin-bottom: 8px;
  font-weight: 500;
`;

const PromoInputWrapper = styled.div`
  display: flex;
  gap: 8px;
`;

const PromoInput = styled(Input)`
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-family: 'LTSaeada', Arial, sans-serif;
  font-size: 0.95rem;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #9fe870;
    box-shadow: 0 0 0 3px rgba(159, 232, 112, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

// Info banner
const InfoBanner = styled.div`
  background: linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%);
  border-left: 4px solid #9fe870;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 24px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const InfoBannerIcon = styled.div`
  font-size: 24px;
  flex-shrink: 0;
`;

const InfoBannerContent = styled.div`
  flex: 1;
`;

const InfoBannerTitle = styled.div`
  font-family: 'LTSaeada', Arial, sans-serif;
  font-weight: 600;
  color: #1f2937;
  font-size: 0.95rem;
  margin-bottom: 4px;
`;

const InfoBannerText = styled.div`
  font-family: 'LTSaeada', Arial, sans-serif;
  font-weight: 400;
  color: #4b5563;
  font-size: 0.85rem;
  line-height: 1.5;
`;

function RepairConfirmation({ ...props }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const showModal = useModal();
  const { t } = useTranslation();
  const {
    client,
    course,
    questions,
    capabilities,
    ligtherQuestions,
    devis,
    codepromo,
  } = useSelector((s) => ({
    client: s.client,
    course: s.course,
    devis: s.diag.devis,
    questions: s.diag.questions,
    capabilities: selectCapabilities(
      s,
      new URLSearchParams(window.location.search),
    ),
    ligtherQuestions: selectQuestionsLight(s),
    codepromo: s.course.codepromo,
  }));
  const estimated_duration = useEstimatedDuration();
  const { isFeatureOn } = useContext(FeaturesContext);
  const isResaShop = useResaShop();
  const { lang } = getLang();
  const { selectedShop, position, product_id, slots, photos, course_details } =
    course || {};
  const { partenaire } = useContext(PartenaireContext);
  const zone = useCurrentZone();
  const [loading, setLoading] = useState(false);
  const [paymentSessionId, setPaymentSessionId] = useState(null);
  // Billing address is opt-in: null until the client explicitly picks one, so
  // an untouched confirmation keeps today's POST /courses payload (no regression).
  const [billingAddress, setBillingAddress] = useState(null);
  const [billingPickerOpen, setBillingPickerOpen] = useState(false);

  useEffect(() => {
    // Initialize payment session and user context
    const sessionId = createPaymentSessionId();
    setPaymentSessionId(sessionId);

    if (client && zone) {
      setPaymentUserContext(client, zone);
    }

    logPaymentStep('confirmation_page_loaded', {
      client_id: client && client.id,
      zone_id: zone && zone.id,
      has_slots: slots && slots.length > 0,
      session_id: sessionId,
    });
  }, []);

  // Analytics: Track when users reach the confirmation page
  // This helps measure conversion rates and identify drop-off points in the booking funnel
  useEffect(() => {
    const trackPageView = async () => {
      try {
        const context = {};

        if (zone?.id) {
          context.zone_id = zone.id;
        }

        if (devis?.total_ttc) {
          context.price_estimate = devis.total_ttc;
        }

        await api.post('/analytics/track', {
          step: 'payment_page_viewed',
          context,
        });
      } catch (error) {
        // Silent fail - don't block the user experience
        console.error('Analytics tracking failed:', error);
      }
    };

    trackPageView();
  }, []);

  // Redirect to start if essential data is missing (direct URL access or refresh)
  useEffect(() => {
    if (!client?.id || !slots || slots.length === 0 || !position) {
      console.warn(
        'Missing essential data for confirmation page, redirecting to start',
      );

      // FIX-579: this redirect was silent — a client bounced back to
      // /reparation with no visible message and no trace of why. `missing`
      // says which of the three required pieces was absent.
      const missing = [
        !client?.id && 'client',
        (!slots || slots.length === 0) && 'slots',
        !position && 'position',
      ].filter(Boolean);

      api
        .post('/analytics/track', {
          step: 'slot_validation_failed',
          context: {
            reason: 'confirmation_missing_data',
            zone_id: zone?.id,
            missing,
          },
        })
        .catch(() => {
          // Silent fail - don't block the user experience
        });

      history.push('/reparation');
    }
  }, [client, slots, position, history]);

  // What the course will actually be invoiced to — mirrors the API cascade.
  // The default one is what applies while `billingAddress` stays null, and is
  // also what the picker offers as its revert-to-default row.
  const billedParty = useMemo(
    () => resolveBilledParty({ billingAddress, client, position }),
    [billingAddress, client, position],
  );
  const defaultBilledParty = useMemo(
    () => resolveBilledParty({ billingAddress: null, client, position }),
    [client, position],
  );

  // FIX-94 — demande chevauchante. The date we are about to book, computed
  // through the very helper that feeds the POST /courses payload below so the
  // question asked to the api can never drift from the course created.
  const courseDate = useMemo(() => buildCourseDate(slots), [slots]);
  // Day only: that is the shape the api validates the `date` param against,
  // and the only part its rule looks at.
  const { getOverlappingCourse } = useOverlappingCourse({
    clientId: client && client.id,
    date: toCourseDay(courseDate),
  });
  // "Warned once per passage on the confirmation screen". Without it, closing
  // the popup with the cross would re-trigger it on every click and turn an
  // informative warning into a blocking one — the opposite of the intent.
  const overlappingWarnedRef = useRef(false);
  // FIX-575: "Réserver sans code" re-invokes handleValidateAppointment from
  // the closure captured when the error modal was built — the SET_CODEPROMO
  // dispatch it fires alongside won't be visible in that closure's `course`
  // until the next render. A ref is mutable across renders, so reading it at
  // call time (not at closure-creation time) sees the flag reliably.
  const forceNoCodeRef = useRef(false);

  // Early return to prevent SSR errors when data is missing
  if (!client?.id || !slots || slots.length === 0 || !position) {
    return null;
  }

  const _sendPic = async (id, file) => {
    await api.postImage(`/courses/${id}/photos`, file);
  };

  const getCourseParams = () => {
    const aggloSlts = aggloSlots(course.slots);
    const aggloSltsKeys = Object.keys(aggloSlts);

    if (aggloSltsKeys.length === 0) {
      console.error('No slots available in aggloSlots', {
        course_slots: course.slots,
        course_state: course,
      });

      logPaymentError(
        'No slots available - user reached confirmation without selecting time slot',
        'missing_time_slot',
        {
          client_id: client && client.id,
          zone_id: zone && zone.id,
          course_slots: course.slots,
          session_id: getPaymentSessionId(),
        },
      );

      return null;
    }

    const defaultDate = aggloSltsKeys[0];
    const defaultSlots = aggloSlts[defaultDate];

    if (!defaultSlots || defaultSlots.length === 0) {
      console.error('No default slots available');
      return null;
    }

    // Flux autodiag multibike (RepairDetails.jsx) — chaque vélo additionnel a
    // sa propre `bike.description` saisie dans un textarea distinct. On les
    // aplatit dans course_details et on préfixe `!Course multi-velos!` (lu
    // côté API par RebookCourseDetailsBuilder::hasUserContent comme marqueur
    // "template vide"). Les autres flux (devis-v3 notamment) n'utilisent pas
    // `bike.description` — on ne touche pas à course_details pour eux.
    const bikeDescriptions = (course.bikes || [])
      .map((bike) => bike.description)
      .filter((description) => description && description.trim() !== '');
    if (bikeDescriptions.length > 0) {
      course.course_details = `!Course multi-velos!\n\n${[
        course.course_details,
        ...bikeDescriptions,
      ]
        .filter((s) => s && s.trim() !== '')
        .join('\n\n')}`;
    }

    const courseParams = {
      position: course.position,
      booking_uuid: course.booking_uuid,
      source_visit_report_id: course.source_visit_report_id || null,
      bike_details: diagToText(questions),
      bikes: course.bikes,
      course_details: course.course_details,
      product_id: course.product_id,
      date: courseDate,
      available_min: defaultSlots[0].min,
      available_max: defaultSlots[defaultSlots.length - 1].max,
      // FIX-575: a code left blank or only whitespace must never be sent as
      // if a code had been entered — the API treats any non-empty string as
      // a code to validate. `forceNoCodeRef` wins over the field's current
      // value: it is how "Réserver sans code" retries without waiting for
      // the SET_CODEPROMO dispatch to reach this closure's redux state.
      codepromo: forceNoCodeRef.current ? '' : (course.codepromo || '').trim(),
      client: { id: client.id },
      enregistrement_type: 'FORMULAIRE',
      paiement: 'ONLINE',
      noclient: course.fixed === 0 ? 1 : 0,
      lieu_type: partenaire,
      slots: course.slots,
      capabilities,
      autodiag: ligtherQuestions,
    };

    if (estimated_duration > 0) {
      courseParams.ed = estimated_duration;
      if (getEstimatedDuration(devis.quote_elements) > 0) {
        courseParams.ed = getEstimatedDuration(devis.quote_elements);
      }
    }
    if (course.fixerId) {
      courseParams.fixer_id = course.fixerId;
    } else if (isResaShop) {
      courseParams.shop_id = selectedShop.id;
      courseParams.paiement = 'ONSITE';
    }

    // #30 — rebook-from-devis sub-selection (cross-repo contract). When the
    // recap screen recorded a per-vélo selection, forward it so the created
    // fiche de course + amount reflect ONLY the checked prestation/piece lines.
    // WIRE FIELD = `selection`; pivot ids = `source_visit_report_velo_ids`.
    // Derivation lives in a shared pure helper so the contract surface can't
    // drift between this call site and its tests. `estimated_amount_ttc` is
    // intentionally NOT sent: the selection carries only ids, and the api is
    // the sole price authority (it recomputes from the filtered lines).
    Object.assign(courseParams, buildDevisSelectionPayload(course));

    // #395 — optional billing address. Adds `billing_address_id` ONLY when the
    // client picked one; absent otherwise so the payload is byte-identical to
    // today's for the untouched flow. Ownership is enforced api-side (anti-IDOR).
    Object.assign(
      courseParams,
      buildBillingAddressPayload(billingAddress && billingAddress.id),
    );

    // Consumed once: a later manual retry (own code re-typed, unrelated
    // field fixed) must go through the field's real value again.
    forceNoCodeRef.current = false;

    return courseParams;
  };

  // FIX-94 — opens the existing request in a NEW TAB: the funnel stays intact
  // behind the popup. Kept out of the render path (SSR-safe by construction).
  const openOverlappingCourse = (courseId) => {
    if (typeof window === 'undefined') return;
    window.open(`/courses/${courseId}`, '_blank', 'noopener');
  };

  // Informative and non-blocking: both buttons AND the cross leave the client
  // free to book. The wording announces a day, not an hour — the api rule
  // compares days.
  const showOverlappingCourseModal = (overlappingCourse, onContinue) => {
    const day = moment(overlappingCourse.date);
    const readableDay = day.isValid() ? day.format('dddd D MMMM YYYY') : null;

    showModal({
      title: 'Vous avez déjà une demande en cours',
      content: readableDay
        ? `Il semblerait que vous ayez déjà une demande prévue le ${readableDay}. Vous pouvez continuer et créer une nouvelle demande, ou consulter votre demande existante avant de confirmer.`
        : 'Il semblerait que vous ayez déjà une demande en cours. Vous pouvez continuer et créer une nouvelle demande, ou consulter votre demande existante avant de confirmer.',
      showModal: true,
      icon: <SurprisedFaceColor56 />,
      showButton: [
        {
          text: 'Voir ma demande existante',
          variant: 'outlinedBlack',
          datatestid: 'overlapping-course-see',
          onClick: () => openOverlappingCourse(overlappingCourse.id),
        },
        {
          text: 'Continuer quand même',
          datatestid: 'overlapping-course-continue',
          onClick: onContinue,
        },
      ],
    });
  };

  const handleValidateAppointment = async () => {
    const sessionId = getPaymentSessionId();

    logPaymentStep('confirmation_validate_clicked', {
      client_id: client && client.id,
      zone_id: zone && zone.id,
      session_id: sessionId,
    });

    setLoading(true);

    try {
      const courseParams = getCourseParams();

      if (!courseParams) {
        showModal({
          title: 'Créneau horaire manquant',
          content:
            'Veuillez sélectionner un créneau horaire avant de continuer. Vous allez être redirigé vers la page de sélection.',
          showModal: true,
          icon: <SurprisedFaceColor56 />,
          showButton: [
            {
              text: 'Retour à la sélection',
              onClick: () => {
                history.push('/reparation/date');
              },
            },
          ],
        });

        setLoading(false);
        return;
      }

      // FIX-94 — warn (once) when the client already has an active request on
      // or after this date. The api owns the rule; we only read the answer,
      // and every failure mode reads as "no overlap" (fail-open): this feature
      // must never be able to prevent a booking.
      const overlappingCourse = await getOverlappingCourse();

      if (overlappingCourse && !overlappingWarnedRef.current) {
        overlappingWarnedRef.current = true;

        // Continuing re-enters this handler from the top, so a second
        // `confirmation_validate_clicked` follows. Without these two steps the
        // breadcrumb trail cannot tell "warned, then accepted" from a plain
        // double-click — and nothing records that the warning fired at all.
        logPaymentStep('confirmation_overlapping_warned', {
          client_id: client && client.id,
          overlapping_course_id: overlappingCourse.id,
          session_id: sessionId,
        });

        showOverlappingCourseModal(overlappingCourse, () => {
          logPaymentStep('confirmation_overlapping_continued', {
            client_id: client && client.id,
            overlapping_course_id: overlappingCourse.id,
            session_id: sessionId,
          });
          handleValidateAppointment();
        });
        // No course is created here — closing with the cross cancels, which is
        // exactly the outcome the warning exists to protect.
        setLoading(false);
        return;
      }

      logPaymentStep('confirmation_course_params_built', {
        client_id: client && client.id,
        has_photos: photos && photos.length > 0,
        photo_count: (photos && photos.length) || 0,
        session_id: sessionId,
      });

      courseParams.version = getVersion();

      logPaymentStep('confirmation_course_api_call_started', {
        client_id: client && client.id,
        zone_id: zone && zone.id,
        session_id: sessionId,
      });

      const newCourse = await api.post(`/courses`, courseParams);

      logPaymentStep('confirmation_course_api_response_received', {
        client_id: client && client.id,
        course_id: newCourse && newCourse.id,
        has_error: !!newCourse && newCourse.error_message,
        error_message: newCourse && newCourse.error_message,
        session_id: sessionId,
      });

      if (newCourse.id) {
        logPaymentStep('confirmation_course_created_success', {
          client_id: client && client.id,
          course_id: newCourse.id,
          zone_id: zone && zone.id,
          session_id: sessionId,
        });

        if (photos && photos.length) {
          logPaymentStep('confirmation_photos_upload_started', {
            client_id: client && client.id,
            course_id: newCourse.id,
            photo_count: photos.length,
            session_id: sessionId,
          });

          await Promise.all(photos.map((file) => _sendPic(newCourse.id, file)));

          logPaymentStep('confirmation_photos_uploaded', {
            client_id: client && client.id,
            course_id: newCourse.id,
            session_id: sessionId,
          });
        }

        GTMAnalyticsSendDL({
          event: 'Purchase',
          course_id: newCourse.id,
        });
        GTMAnalyticsSendDL({
          event: 'pageview',
          url: '/reparation/thankyou',
        });

        // Réinitialiser le funnel après création réussie de la course
        analytics.resetFunnel();

        const redirectUrl = `/courses/${newCourse.id}/mercipourvotredemande`;

        logPaymentStep('confirmation_redirect_to_thankyou', {
          client_id: client && client.id,
          course_id: newCourse.id,
          redirect_url: redirectUrl,
          session_id: sessionId,
        });

        setTimeout(() => {
          history.push(redirectUrl);
        }, 200);
        setTimeout(() => {
          clearPaymentSession();
          dispatch({
            type: RESET_COURSE,
            full: true,
          });
        }, 300);
      } else if (newCourse.error_message) {
        logPaymentError(
          newCourse.error_message,
          'confirmation_course_creation_failed',
          {
            client_id: client && client.id,
            zone_id: zone && zone.id,
            errors: newCourse.errors,
            session_id: sessionId,
          },
        );

        const {
          title,
          content,
          isCodepromoOnly,
        } = buildCourseCreationErrorModal(newCourse);

        // FIX-575: a rejected codepromo must not be a dead end. Retrying
        // clears the field first — resubmitting with the same value would
        // just fail the same way again.
        const retryWithoutCode = () => {
          forceNoCodeRef.current = true;
          dispatch({
            type: SET_CODEPROMO,
            codepromo: '',
          });
          handleValidateAppointment();
        };

        showModal({
          title,
          content,
          showModal: true,
          icon: <SurprisedFaceColor56 />,
          showButton: isCodepromoOnly
            ? [
                {
                  text: t(`close.cta`),
                  variant: 'outlinedBlack',
                },
                {
                  text: 'Réserver sans code',
                  datatestid: 'course-creation-error-retry-without-code',
                  onClick: retryWithoutCode,
                },
              ]
            : [
                {
                  text: t(`close.cta`),
                },
              ],
        });

        setLoading(false);
      }
    } catch (error) {
      logPaymentError(error, 'confirmation_course_creation_failed', {
        client_id: client && client.id,
        zone_id: zone && zone.id,
        session_id: sessionId,
        error_stack: error && error.stack,
      });

      showModal({
        title: 'Erreur lors de la création de la réservation',
        content:
          (error && error.message) ||
          'Une erreur inattendue est survenue. Veuillez réessayer.',
        showModal: true,
        icon: <SurprisedFaceColor56 />,
        showButton: [
          {
            text: t(`close.cta`),
          },
        ],
      });

      setLoading(false);
    }
  };

  // Format date and time for display
  // Returns an array of all selected days with their time ranges
  const getFormattedDateTime = () => {
    if (!slots || slots.length === 0) return null;
    const aggloSlts = aggloSlots(slots);
    const dates = Object.keys(aggloSlts);
    if (dates.length === 0) return null;

    // Helper to format time: "08:00" -> "08h00"
    const formatTime = (time) => time.replace(':', 'h');

    return dates.map((date) => {
      const daySlots = aggloSlts[date];
      const formattedDate = moment(date).format('dddd D MMMM YYYY');
      const timeRange = `${formatTime(daySlots[0].min)} - ${formatTime(
        daySlots[daySlots.length - 1].max,
      )}`;
      return { date: formattedDate, time: timeRange };
    });
  };

  const dateTimes = getFormattedDateTime();

  return (
    <Container
      fluid
      style={{
        background: '#f9fafb',
        minHeight: '100vh',
        paddingTop: '40px',
        paddingBottom: '80px',
      }}>
      <Container>
        {/* Main Card */}
        <MainCard>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <Heading
              level={2}
              style={{
                fontFamily: 'LTSaeada, Arial, sans-serif',
                fontWeight: 700,
                color: '#1f2937',
                lineHeight: 1.2,
                fontSize: '2rem',
                marginBottom: '12px',
              }}>
              Dernière étape,{' '}
              <FirstLetterUpperCase>
                {client?.prenom?.replace(/\s/g, '') || 'vous'}
              </FirstLetterUpperCase>{' '}
              ! 🎉
            </Heading>
            <Text
              style={{
                color: '#6b7280',
                fontSize: '1.05rem',
                lineHeight: 1.6,
              }}>
              Nos réparateurs ont hâte de remettre votre vélo sur la route 🚲
            </Text>
          </div>

          {/* Info Banner - No Payment */}
          <InfoBanner>
            <InfoBannerIcon>💳</InfoBannerIcon>
            <InfoBannerContent>
              <InfoBannerTitle>Aucun paiement aujourd'hui</InfoBannerTitle>
              <InfoBannerText>
                Le paiement n'interviendra qu'une fois la réparation terminée.
                Devis établi sur place.
              </InfoBannerText>
            </InfoBannerContent>
          </InfoBanner>

          {/* Summary Section */}
          <div style={{ marginBottom: '32px' }}>
            <h3
              style={{
                fontFamily: 'LTSaeada, Arial, sans-serif',
                fontSize: '1.1rem',
                fontWeight: 600,
                color: '#1f2937',
                marginBottom: '16px',
              }}>
              Récapitulatif de votre demande
            </h3>

            {/* Repair Type */}
            <SummaryItem>
              <SummaryIcon>🔧</SummaryIcon>
              <SummaryContent>
                <SummaryLabel>Type de réparation</SummaryLabel>
                <SummaryValue>
                  {product_id === 1
                    ? 'Réparation de vélo'
                    : 'Réparation de trottinette'}
                  {questions && questions.length > 0 && (
                    <div
                      style={{
                        fontSize: '0.9rem',
                        color: '#6b7280',
                        marginTop: '4px',
                      }}>
                      {questions.length} problème
                      {questions.length > 1 ? 's' : ''} identifié
                      {questions.length > 1 ? 's' : ''}
                    </div>
                  )}
                </SummaryValue>
              </SummaryContent>
            </SummaryItem>

            {/* Description du besoin (depuis le recap-devis ou la diag) */}
            {course_details && course_details.trim() !== '' && (
              <SummaryItem data-testid="repair-confirmation-course-details">
                <SummaryIcon>📝</SummaryIcon>
                <SummaryContent>
                  <SummaryLabel>Description du besoin</SummaryLabel>
                  <SummaryValue style={{ whiteSpace: 'pre-line' }}>
                    {course_details}
                  </SummaryValue>
                </SummaryContent>
              </SummaryItem>
            )}

            {/* Address */}
            <SummaryItem>
              <SummaryIcon>📍</SummaryIcon>
              <SummaryContent>
                <SummaryLabel>Adresse d'intervention</SummaryLabel>
                <SummaryValue>{position}</SummaryValue>
              </SummaryContent>
            </SummaryItem>

            {/* Billing address (optional, #395) */}
            <SummaryItem>
              <SummaryIcon>🧾</SummaryIcon>
              <SummaryContent>
                <SummaryLabel>
                  {t('repairconfirmation.billing.label')}
                </SummaryLabel>
                <SummaryValue>
                  {billedParty.name}
                  <div
                    style={{
                      fontSize: '0.9rem',
                      color: '#6b7280',
                      marginTop: '4px',
                    }}>
                    {billedParty.lines.join(', ')}
                    {billedParty.noteKey && (
                      <div style={{ marginTop: '2px' }}>
                        {t(billedParty.noteKey)}
                      </div>
                    )}
                  </div>
                </SummaryValue>
                <Link
                  color="blue.electric"
                  mt={2}
                  onClick={() => setBillingPickerOpen(true)}>
                  {t('repairconfirmation.billing.cta')}
                </Link>
              </SummaryContent>
            </SummaryItem>
            <BillingAddressPicker
              open={billingPickerOpen}
              onRequestClose={() => setBillingPickerOpen(false)}
              clientId={client.id}
              selectedId={billingAddress && billingAddress.id}
              defaultOption={defaultBilledParty}
              onSelect={setBillingAddress}
            />

            {/* Date & Time */}
            {dateTimes && dateTimes.length > 0 && (
              <SummaryItem>
                <SummaryIcon>📅</SummaryIcon>
                <SummaryContent>
                  <SummaryLabel>
                    Date{dateTimes.length > 1 ? 's' : ''} et créneau
                    {dateTimes.length > 1 ? 'x' : ''}
                  </SummaryLabel>
                  <SummaryValue>
                    {dateTimes.map((dt, index) => (
                      <div
                        key={dt.date}
                        style={{
                          marginBottom:
                            index < dateTimes.length - 1 ? '8px' : 0,
                        }}>
                        <FirstLetterUpperCase>{dt.date}</FirstLetterUpperCase>
                        <div
                          style={{
                            fontSize: '0.9rem',
                            color: '#6b7280',
                            marginTop: '4px',
                          }}>
                          {dt.time}
                        </div>
                      </div>
                    ))}
                  </SummaryValue>
                </SummaryContent>
              </SummaryItem>
            )}
          </div>

          {/* Promo Code */}
          <PromoCodeGroup>
            <PromoLabel>🎫 {t('repaircheckout.discount.label')}</PromoLabel>
            <PromoInputWrapper>
              <PromoInput
                name="codepromo"
                placeholder="Entrez votre code"
                onChange={(e) =>
                  dispatch({
                    type: SET_CODEPROMO,
                    codepromo: e.target.value,
                  })
                }
                value={codepromo}
              />
            </PromoInputWrapper>
          </PromoCodeGroup>

          {/* CTA Button - Desktop only (hidden on mobile) */}
          <InlineCTAWrapper style={{ marginTop: '32px' }}>
            <PrimaryCTA onClick={handleValidateAppointment} disabled={loading}>
              {loading ? (
                <>
                  <Spinner />
                  Validation en cours...
                </>
              ) : (
                <>✓ Oui, je confirme ce rendez-vous</>
              )}
            </PrimaryCTA>
            <CgvConsent />
          </InlineCTAWrapper>

          {/* Trust Badges */}
          <TrustBadges>
            <TrustBadge>
              <span>💶</span>
              Bonus Répar jusqu'à 30€
            </TrustBadge>
            <TrustBadge>
              <span>🛡️</span>
              Assurance AXA
            </TrustBadge>
            <TrustBadge>
              <span>⚡</span>
              Paiement après réparation
            </TrustBadge>
          </TrustBadges>

          {/* Additional Info */}
          <div
            style={{
              marginTop: '24px',
              padding: '16px',
              background: '#f9fafb',
              borderRadius: '8px',
              textAlign: 'center',
              marginBottom: '80px', // Extra space for mobile fixed footer
            }}>
            <Text
              style={{
                fontSize: '0.85rem',
                color: '#6b7280',
                lineHeight: 1.5,
              }}>
              🔒 Annulation gratuite jusqu'à 2h avant le rendez-vous
            </Text>
          </div>
          <MobileFooterSpacer />
        </MainCard>
      </Container>

      {/* Fixed Footer - Mobile only */}
      <MobileFixedFooter>
        <PrimaryCTA onClick={handleValidateAppointment} disabled={loading}>
          {loading ? (
            <>
              <Spinner />
              Validation en cours...
            </>
          ) : (
            <>✓ Confirmer ce rendez-vous</>
          )}
        </PrimaryCTA>
        <CgvConsent />
      </MobileFixedFooter>
    </Container>
  );
}

export default RepairConfirmation;
